import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'
import { usePrivacySettings, useUpdatePrivacy } from '../hooks/usePrivacy'
import { PRIVACY_LEVELS, type PrivacyLevelValue } from '../types'

/**
 * Choix du niveau de confidentialité (spécifications § 7.2).
 *
 * Le niveau renvoyé par l'API est déjà le niveau effectif : sous treize ans il
 * vaut « privé » et le choix est verrouillé, quelle que soit la préférence
 * enregistrée. Le formulaire reflète cette décision au lieu de la contourner.
 */
export function PrivacySettings() {
  const { t } = useTranslation()
  const { data: settings, isLoading, isError } = usePrivacySettings()
  const update = useUpdatePrivacy()

  if (isLoading) {
    return <Spinner size="md" />
  }

  if (isError || !settings) {
    return <Alert variant="error">{t('social.privacy.load_error')}</Alert>
  }

  const locked = !settings.can_choose

  return (
    <section aria-labelledby="social-privacy-title" className="flex flex-col gap-4">
      <div>
        <h2
          id="social-privacy-title"
          className="font-display text-lg font-semibold text-on-surface"
        >
          {t('social.privacy.title')}
        </h2>
        <p className="mt-1 text-sm text-on-surface-variant">{t('social.privacy.subtitle')}</p>
      </div>

      {locked && <Alert variant="info">{t('social.privacy.locked_minor')}</Alert>}
      {!settings.friends_enabled && <Alert variant="warning">{t('social.privacy.friends_locked')}</Alert>}
      {update.isError && <Alert variant="error">{t('social.privacy.update_error')}</Alert>}
      {update.isSuccess && <Alert variant="success">{t('social.privacy.updated')}</Alert>}

      <fieldset className="flex flex-col gap-2" disabled={locked || update.isPending}>
        <legend className="sr-only">{t('social.privacy.title')}</legend>

        {PRIVACY_LEVELS.map((level: PrivacyLevelValue) => (
          <label
            key={level}
            className={[
              'flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition-colors',
              settings.level === level
                ? 'border-primary bg-primary/5'
                : 'border-outline-variant bg-surface',
              locked ? 'cursor-not-allowed opacity-60' : '',
            ].join(' ')}
          >
            <input
              type="radio"
              name="privacy-level"
              value={level}
              checked={settings.level === level}
              onChange={() => update.mutate(level)}
              className="mt-1 h-4 w-4 shrink-0 accent-primary"
            />
            <span className="flex flex-col">
              <span className="text-sm font-semibold text-on-surface">
                {t(`social.privacy.levels.${level}.name`)}
              </span>
              <span className="text-xs leading-snug text-on-surface-variant">
                {t(`social.privacy.levels.${level}.description`)}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
    </section>
  )
}
